import { createSelector } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux'
import { RootState } from './root'
import { getUsersState, SubUserModulePermission } from './sendhub-users/sendhub-users.slice'

declare global {
  interface Window {
    SH?: {
      models?: {
        UserProfileSingleton?: {
          hasSubuserModuleAccess?: (module: string, level?: string) => boolean
          isSubUser?: () => boolean
        }
      }
    }
  }
}

export type IntegrationsPermissionLevel = 'view' | 'edit' | 'delete'

export const SUBUSER_INTEGRATIONS_ACCESS_DENIED_MESSAGE = 'This is not allowed. Please get the access from your admin.'

export type { SubUserModulePermission }

function grantsForModule(permissions: SubUserModulePermission[] | undefined, moduleKey: string): Record<string, boolean> {
  const grants: Record<string, boolean> = {}
  for (const perm of permissions || []) {
    if (!perm?.module || !perm?.level) continue
    const key = perm.module === 'threads' ? 'inbox' : perm.module
    if (key !== moduleKey) continue
    grants[String(perm.level).toLowerCase()] = true
  }
  return grants
}

export function hasModuleGrant(
  permissions: SubUserModulePermission[] | undefined,
  moduleKey: string,
  minLevel: IntegrationsPermissionLevel = 'view'
): boolean {
  const g = grantsForModule(permissions, moduleKey)
  if (minLevel === 'view') return Object.keys(g).length > 0
  if (minLevel === 'edit') return !!g['edit']
  if (minLevel === 'delete') return !!g['delete']
  return false
}

function accessFromWindow(minLevel: IntegrationsPermissionLevel): boolean | null {
  const profile = window.SH?.models?.UserProfileSingleton
  if (!profile?.hasSubuserModuleAccess) return null
  if (typeof profile.isSubUser === 'function' && !profile.isSubUser()) {
    return true
  }
  return profile.hasSubuserModuleAccess('integrations', minLevel)
}

export const selectSubUserPermissions = createSelector(getUsersState, (state) => state.subUserPermissions)

export const selectIsSubUser = createSelector(getUsersState, (state) => {
  if (window.SH?.models?.UserProfileSingleton?.isSubUser) {
    return window.SH.models.UserProfileSingleton.isSubUser()
  }
  return (state.subUserPermissions || []).length > 0
})

export function selectCanIntegrations(minLevel: IntegrationsPermissionLevel = 'view') {
  return (state: RootState): boolean => {
    const fromWindow = accessFromWindow(minLevel)
    if (fromWindow !== null) return fromWindow
    const permissions = selectSubUserPermissions(state)
    // Not fetched yet: view/edit keep their existing permissive default (the
    // whole page shouldn't lock up while this loads), but delete is
    // destructive enough that it must not default to allowed just because we
    // don't know yet. Once fetchUsers() resolves, an owner's permissions come
    // back as an empty array (not undefined) and correctly falls through to
    // the "no sub-user restrictions" case below.
    if (permissions === undefined) return minLevel !== 'delete'
    if (permissions.length === 0) return true
    return hasModuleGrant(permissions, 'integrations', minLevel)
  }
}

export function useIntegrationsAccess() {
  const canView = useSelector(selectCanIntegrations('view'))
  const canEdit = useSelector(selectCanIntegrations('edit'))
  const canDelete = useSelector(selectCanIntegrations('delete'))
  return { canView, canEdit, canDelete }
}
